import { makeAutoObservable } from "mobx";
import { supabase } from "@/utils/supabase";
/**
 * Observer for per-order realtime updates.
 * Mirrors TechnicianLocationObserver teardown pattern but uses a
 * PER-ORDER channel name (`order_detail_${orderId}`) to avoid collisions.
 *
 * Lifecycle:
 *   start(orderId, onUpdate) → subscribes to orders UPDATE for that id
 *   stop()                   → removeChannel, nulls channel, sets inactive
 *   destroy()                → stop()
 *
 * The onUpdate callback is intentionally simple (no payload forwarding):
 * the store re-fetches the full row from v_order_details on every trigger.
 */
export class OrderDetailObserver {
    constructor() {
        this.isActive = false;
        this.channel = null;
        makeAutoObservable(this);
    }
    /**
     * Start listening for UPDATE events on the orders row for this orderId.
     * Guards against double-start.
     */
    start(orderId, onUpdate) {
        if (this.isActive) {
            console.warn("OrderDetailObserver: already active for an order — call stop() first");
            return;
        }
        this.channel = supabase()
            .channel(`order_detail_${orderId}`)
            .on("postgres_changes", {
            event: "UPDATE",
            schema: "public",
            table: "orders",
            filter: `id=eq.${orderId}`,
        }, () => onUpdate())
            .on("postgres_changes", {
            event: "*",
            schema: "public",
            table: "order_photos",
            filter: `order_id=eq.${orderId}`,
        }, () => onUpdate())
            .subscribe((status) => {
            if (status === "CHANNEL_ERROR") {
                console.error(`OrderDetailObserver: channel error for order ${orderId}`);
            }
        });
        this.isActive = true;
    }
    /**
     * Stop the realtime subscription and clean up.
     * Safe to call when already inactive.
     */
    stop() {
        if (!this.isActive) {
            return;
        }
        if (this.channel) {
            supabase().removeChannel(this.channel);
            this.channel = null;
        }
        this.isActive = false;
    }
    /**
     * Alias for stop() — called by the store on unmount.
     */
    destroy() {
        this.stop();
    }
}
